import { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import {
  MapPin,
  CreditCard,
  Banknote,
  AlertCircle,
  Plus,
  ShieldCheck,
  Truck,
  Store,
  ArrowRight,
  X,
  Lock,
} from 'lucide-react'
import {
  api,
  formatMoney,
  generateUUID,
  storeIdFromEnv,
  type Address,
  type CheckoutQuote,
  type SlotView,
} from '../api/client'
import { useCart } from '../state/cart'
import { useSession } from '../state/session'
import { OrderSuccessModal, type SuccessOrderDetails } from '../components/OrderSuccessModal'
import { useTranslation } from '../i18n/LanguageContext'

export function Checkout() {
  const { t, isHindi } = useTranslation()
  const { cart, refresh: refreshCart } = useCart()
  const { signedIn, phoneNumber, fullName } = useSession()
  const navigate = useNavigate()

  const [fulfilment, setFulfilment] = useState<'DELIVERY' | 'PICKUP'>('DELIVERY')
  const [addresses, setAddresses] = useState<Address[]>([])
  const [addressId, setAddressId] = useState<string | undefined>(undefined)
  const [date, setDate] = useState<string>(() => new Date().toISOString().slice(0, 10))
  const [slots, setSlots] = useState<SlotView[]>([])
  const [slotId, setSlotId] = useState<string | undefined>(undefined)
  const [paymentMethod, setPaymentMethod] = useState<'ONLINE' | 'CASH_ON_DELIVERY'>('ONLINE')
  const [quote, setQuote] = useState<CheckoutQuote | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [confirmedOrder, setConfirmedOrder] = useState<SuccessOrderDetails | null>(null)
  const [showSuccessModal, setShowSuccessModal] = useState(false)

  // Add Address Modal state
  const [showAddressModal, setShowAddressModal] = useState(false)
  const [newRecipient, setNewRecipient] = useState(fullName || '')
  const [newPhone, setNewPhone] = useState(phoneNumber || '')
  const [newLine1, setNewLine1] = useState('')
  const [newLine2, setNewLine2] = useState('')
  const [newCity, setNewCity] = useState('Mumbai')
  const [newState, setNewState] = useState('Maharashtra')
  const [newPincode, setNewPincode] = useState('400001')
  const [addressBusy, setAddressBusy] = useState(false)
  const [addressError, setAddressError] = useState<string | null>(null)

  useEffect(() => {
    if (!signedIn) return

    api.addresses
      .list()
      .then((list) => {
        setAddresses(list)
        const def = list.find((a) => a.defaultAddress)?.id ?? list[0]?.id
        setAddressId(def)
      })
      .catch(() => setAddresses([]))
  }, [signedIn])

  useEffect(() => {
    if (!storeIdFromEnv()) return

    api.checkout
      .slots(date, fulfilment)
      .then((day) => {
        setSlots(day.slots)
        setSlotId(day.slots.find((slot) => slot.bookable)?.id)
      })
      .catch(() => setSlots([]))
  }, [date, fulfilment])

  // Re-quote when choices change
  useEffect(() => {
    if (!signedIn) return

    setBusy(true)

    api.checkout
      .quote(fulfilment, addressId, slotId)
      .then((result) => {
        setQuote(result)
        setError(null)

        const firstAvailable = result.paymentOptions.find((option) => option.available)
        if (firstAvailable) {
          setPaymentMethod(firstAvailable.method)
        }
      })
      .catch((failure: Error) => setError(failure.message))
      .finally(() => setBusy(false))
  }, [fulfilment, addressId, slotId, signedIn])

  if (!signedIn) {
    return (
      <div className="card mx-auto my-10 max-w-md p-8 text-center shadow-lg">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700">
          <Lock className="h-8 w-8" />
        </div>
        <h1 className="font-heading text-2xl font-bold text-stone-900">{t('checkout_signin_title')}</h1>
        <p className="mt-2 text-xs leading-relaxed text-stone-500">
          {t('checkout_signin_desc')}
        </p>
        <Link
          to="/sign-in"
          state={{ from: '/checkout', message: isHindi ? 'चेकआउट पूरा करने के लिए साइन इन करें' : 'Sign in to finish your checkout' }}
          className="btn-primary mt-6 inline-flex w-full items-center justify-center gap-2 py-3 text-sm font-bold shadow-md shadow-emerald-700/20"
        >
          <span>{t('checkout_signin_btn')}</span>
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    )
  }

  if ((!cart || cart.lines.length === 0) && !showSuccessModal) {
    return (
      <div className="card mx-auto my-12 max-w-md p-8 text-center">
        <h1 className="font-heading text-xl font-bold text-stone-900">{t('cart_empty_title')}</h1>
        <p className="mt-1 text-xs text-stone-500">{t('checkout_empty_cart_msg')}</p>
        <Link to="/" className="btn-primary mt-4 inline-block">
          {t('cart_start_shopping')}
        </Link>
      </div>
    )
  }

  async function handleCreateAddress(e: React.FormEvent) {
    e.preventDefault()
    setAddressBusy(true)
    setAddressError(null)

    try {
      const addressPayload: Omit<Address, 'id' | 'defaultAddress'> & { makeDefault?: boolean } = {
        recipientName: newRecipient.trim(),
        phoneNumber: newPhone.trim(),
        line1: newLine1.trim(),
        city: newCity.trim(),
        state: newState.trim(),
        pincode: newPincode.trim(),
        makeDefault: true,
      }
      if (newLine2.trim()) {
        addressPayload.line2 = newLine2.trim()
      }

      const created = await api.addresses.create(addressPayload)

      setAddresses((prev) => [created, ...prev])
      setAddressId(created.id)
      setShowAddressModal(false)
      setNewLine1('')
      setNewLine2('')
    } catch (err) {
      setAddressError(err instanceof Error ? err.message : 'Could not save address.')
    } finally {
      setAddressBusy(false)
    }
  }

  async function placeOrder() {
    if (!quote || !slotId) return

    setBusy(true)
    setError(null)

    const idempotencyKey = generateUUID()

    // Build payload respecting exactOptionalPropertyTypes
    const payload: {
      fulfilment: 'DELIVERY' | 'PICKUP'
      addressId?: string
      slotId: string
      paymentMethod: 'ONLINE' | 'CASH_ON_DELIVERY'
      expectedTotalPaise: number
      quoteId?: string
      customerNote?: string
    } = {
      fulfilment,
      slotId,
      paymentMethod,
      expectedTotalPaise: quote.totals.totalPaise,
    }

    if (addressId) payload.addressId = addressId
    if (quote.quoteId) payload.quoteId = quote.quoteId

    try {
      const order = await api.orders.place(payload, idempotencyKey)
      await refreshCart()

      // Enrich order with address and slot label if missing
      const selectedAddress = addresses.find((a) => a.id === addressId)
      const selectedSlot = slots.find((s) => s.id === slotId)
      const enrichedOrder: SuccessOrderDetails = {
        orderId: order.orderId,
        orderNumber: order.orderNumber,
        totalPaise: order.totalPaise,
        paymentMethod: order.paymentMethod,
        paymentStatus: order.paymentStatus,
        fulfilmentType: order.fulfilmentType,
        itemCount: order.itemCount,
      }
      if (order.deliveryAddress) {
        enrichedOrder.deliveryAddress = order.deliveryAddress
      } else if (selectedAddress) {
        enrichedOrder.deliveryAddress = {
          recipientName: selectedAddress.recipientName,
          phoneNumber: selectedAddress.phoneNumber,
          formattedSingleLine: [selectedAddress.line1, selectedAddress.line2, selectedAddress.city, selectedAddress.pincode].filter(Boolean).join(', '),
        }
      }
      if (order.window) {
        enrichedOrder.window = order.window
      } else if (selectedSlot) {
        enrichedOrder.window = {
          slotId: selectedSlot.id,
          date: selectedSlot.date,
          label: `${selectedSlot.startTime} - ${selectedSlot.endTime}`,
        }
      }

      if (paymentMethod === 'ONLINE') {
        try {
          const initiation = await api.payments.initiate(order.orderId, idempotencyKey)
          const keyId = initiation.keyId || import.meta.env.VITE_RAZORPAY_KEY_ID || 'rzp_test_TiXJoQw5fFV0JK'

          if (initiation.gatewayConfigured && initiation.gatewayOrderId && window.Razorpay) {
            const rzp = new window.Razorpay({
              key: keyId,
              amount: initiation.amountPaise,
              currency: initiation.currency || 'INR',
              name: 'FreshGrocer',
              description: `Payment for Order #${order.orderNumber}`,
              order_id: initiation.gatewayOrderId,
              prefill: {
                name: selectedAddress?.recipientName || '',
                contact: selectedAddress?.phoneNumber || '',
              },
              theme: {
                color: '#047857',
              },
              handler: async (response) => {
                try {
                  await api.payments.confirm(
                    response.razorpay_order_id,
                    response.razorpay_payment_id,
                    response.razorpay_signature,
                  )
                  // Online payment confirmed! Show popup modal
                  setConfirmedOrder({
                    ...enrichedOrder,
                    paymentStatus: 'PAID',
                    paymentMethod: 'ONLINE',
                  })
                  setShowSuccessModal(true)
                } catch (confirmErr) {
                  console.error('Payment confirmation error:', confirmErr)
                  navigate(`/orders/${order.orderId}`, { state: { justPlaced: true } })
                }
              },
              modal: {
                ondismiss: () => {
                  navigate(`/orders/${order.orderId}`)
                },
              },
            })
            rzp.open()
            return
          }
        } catch (paymentErr) {
          console.error('Payment initiation error:', paymentErr)
        }
      }

      // Cash on Delivery (or online fallback): Show celebratory confirmation popup!
      setConfirmedOrder(enrichedOrder)
      setShowSuccessModal(true)
    } catch (failure) {
      const message = failure instanceof Error ? failure.message : 'The order could not be placed.'
      setError(message)

      try {
        setQuote(await api.checkout.quote(fulfilment, addressId, slotId))
      } catch {
        // Keep quote in place
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      {/* Title */}
      <div>
        <h1 className="font-heading text-2xl font-black text-stone-900 sm:text-3xl">{t('checkout_title')}</h1>
        <p className="text-xs text-stone-500">
          {isHindi ? 'डिलीवरी का पता, समय और भुगतान कन्फर्म करें' : 'Confirm delivery address, slot, and payment'}
        </p>
      </div>

      {error && (
        <div role="alert" className="flex items-center gap-2 rounded-2xl bg-red-50 p-4 text-xs font-semibold text-red-800 border border-red-200">
          <AlertCircle className="h-4 w-4 shrink-0 text-red-600" />
          <span>{error}</span>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr,360px]">
        {/* Left Column: Checkout Steps */}
        <div className="space-y-6">
          {/* Step 1: Delivery Method */}
          <section className="card p-5 space-y-4">
            <h2 className="flex items-center gap-2 font-heading text-base font-bold text-stone-900">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-xs font-black text-emerald-800">
                1
              </span>
              <span>{t('checkout_step_how')}</span>
            </h2>

            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setFulfilment('DELIVERY')}
                className={`flex items-center gap-3 rounded-2xl border p-3.5 text-left transition-all ${
                  fulfilment === 'DELIVERY'
                    ? 'border-emerald-600 bg-emerald-50/70 ring-2 ring-emerald-500/20'
                    : 'border-stone-200/90 bg-white hover:border-emerald-300'
                }`}
              >
                <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${fulfilment === 'DELIVERY' ? 'bg-emerald-700 text-white' : 'bg-stone-100 text-stone-600'}`}>
                  <Truck className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs font-bold text-stone-900">{t('checkout_home_delivery')}</div>
                  <div className="text-[11px] text-stone-500">{t('checkout_home_delivery_desc')}</div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setFulfilment('PICKUP')}
                className={`flex items-center gap-3 rounded-2xl border p-3.5 text-left transition-all ${
                  fulfilment === 'PICKUP'
                    ? 'border-emerald-600 bg-emerald-50/70 ring-2 ring-emerald-500/20'
                    : 'border-stone-200/90 bg-white hover:border-emerald-300'
                }`}
              >
                <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${fulfilment === 'PICKUP' ? 'bg-emerald-700 text-white' : 'bg-stone-100 text-stone-600'}`}>
                  <Store className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs font-bold text-stone-900">{t('checkout_store_pickup')}</div>
                  <div className="text-[11px] text-stone-500">{t('checkout_store_pickup_desc')}</div>
                </div>
              </button>
            </div>

            {/* Address Selection (if DELIVERY) */}
            {fulfilment === 'DELIVERY' && (
              <div className="mt-4 pt-4 border-t border-stone-100 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="label mb-0">{t('checkout_address_title')}</span>
                  <button
                    type="button"
                    onClick={() => setShowAddressModal(true)}
                    className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 hover:underline"
                  >
                    <Plus className="h-3.5 w-3.5" /> {t('checkout_address_add_new')}
                  </button>
                </div>

                {addresses.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-stone-300 p-6 text-center">
                    <MapPin className="mx-auto h-8 w-8 text-stone-300" />
                    <p className="mt-2 text-xs font-semibold text-stone-700">{t('checkout_no_address')}</p>
                    <p className="text-[11px] text-stone-400">{t('checkout_no_address_desc')}</p>
                    <button
                      type="button"
                      onClick={() => setShowAddressModal(true)}
                      className="btn-primary mt-3 py-1.5 text-xs font-bold"
                    >
                      {t('checkout_add_address_now')}
                    </button>
                  </div>
                ) : (
                  <div className="grid gap-2.5">
                    {addresses.map((address) => {
                      const isSelected = addressId === address.id
                      return (
                        <label
                          key={address.id}
                          className={`flex items-start gap-3 rounded-2xl border p-3.5 cursor-pointer transition-all ${
                            isSelected
                              ? 'border-emerald-600 bg-emerald-50/50 ring-2 ring-emerald-500/10'
                              : 'border-stone-200/90 bg-white hover:border-emerald-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="address"
                            checked={isSelected}
                            onChange={() => setAddressId(address.id)}
                            className="mt-1 h-4 w-4 text-emerald-600 focus:ring-emerald-500"
                          />
                          <div className="flex-1 text-xs">
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-stone-900">{address.recipientName}</span>
                              {address.defaultAddress && (
                                <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-800">
                                  {t('checkout_address_default')}
                                </span>
                              )}
                              <span className="text-stone-400">({address.phoneNumber})</span>
                            </div>
                            <p className="mt-0.5 text-stone-600">
                              {address.line1}{address.line2 ? `, ${address.line2}` : ''}, {address.city}, {address.state} - {address.pincode}
                            </p>
                          </div>
                        </label>
                      )
                    })}
                  </div>
                )}
              </div>
            )}
          </section>

          {/* Step 2: Time Window */}
          <section className="card p-5 space-y-4">
            <h2 className="flex items-center gap-2 font-heading text-base font-bold text-stone-900">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-xs font-black text-emerald-800">
                2
              </span>
              <span>{t('checkout_slot_step')}</span>
            </h2>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="label">{t('checkout_slot_date')}</label>
                <input
                  type="date"
                  className="field py-2 text-xs font-bold"
                  value={date}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={(event) => setDate(event.target.value)}
                />
              </div>
            </div>

            <div>
              <span className="label">{t('checkout_slot_select')}</span>
              <div className="mt-1 flex flex-wrap gap-2">
                {slots.length === 0 ? (
                  <p className="text-xs text-stone-500">
                    {t('checkout_slot_auto')}
                  </p>
                ) : (
                  slots.map((slot) => {
                    const isSelected = slotId === slot.id
                    return (
                      <button
                        key={slot.id}
                        type="button"
                        disabled={!slot.bookable}
                        onClick={() => setSlotId(slot.id)}
                        className={`rounded-xl border px-3.5 py-2 text-xs font-bold transition-all ${
                          isSelected
                            ? 'border-emerald-600 bg-emerald-700 text-white shadow-sm'
                            : 'border-stone-200/90 bg-white text-stone-700 hover:border-emerald-300'
                        } ${slot.bookable ? '' : 'opacity-40 cursor-not-allowed'}`}
                      >
                        <div>
                          {slot.startTime.slice(0, 5)} – {slot.endTime.slice(0, 5)}
                        </div>
                        {slot.full && <span className="text-[10px] text-red-500 font-normal">{t('checkout_slot_full')}</span>}
                      </button>
                    )
                  })
                )}
              </div>
            </div>
          </section>

          {/* Step 3: Payment Method */}
          <section className="card p-5 space-y-4">
            <h2 className="flex items-center gap-2 font-heading text-base font-bold text-stone-900">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-xs font-black text-emerald-800">
                3
              </span>
              <span>{t('checkout_payment_step')}</span>
            </h2>

            <div className="grid gap-3 sm:grid-cols-2">
              {quote?.paymentOptions.map((option) => {
                const isSelected = paymentMethod === option.method
                const isOnline = option.method === 'ONLINE'
                return (
                  <label
                    key={option.method}
                    className={`flex items-start gap-3 rounded-2xl border p-4 cursor-pointer transition-all ${
                      isSelected
                        ? 'border-emerald-600 bg-emerald-50/60 ring-2 ring-emerald-500/10'
                        : 'border-stone-200/90 bg-white hover:border-emerald-300'
                    } ${option.available ? '' : 'opacity-40 cursor-not-allowed'}`}
                  >
                    <input
                      type="radio"
                      name="payment"
                      checked={isSelected}
                      disabled={!option.available}
                      onChange={() => setPaymentMethod(option.method)}
                      className="mt-0.5 h-4 w-4 text-emerald-600 focus:ring-emerald-500"
                    />
                    <div className="flex-1 text-xs">
                      <div className="flex items-center gap-1.5 font-bold text-stone-900">
                        {isOnline ? (
                          <>
                            <CreditCard className="h-4 w-4 text-emerald-700" />
                            <span>{t('checkout_payment_online')}</span>
                          </>
                        ) : (
                          <>
                            <Banknote className="h-4 w-4 text-emerald-700" />
                            <span>{t('checkout_payment_cod')}</span>
                          </>
                        )}
                      </div>
                      <p className="mt-1 text-[11px] text-stone-500">
                        {isOnline
                          ? t('checkout_online_subtitle')
                          : t('checkout_cod_subtitle')}
                      </p>
                      {option.requiresOtpConfirmation && (
                        <p className="mt-1 text-[10px] text-amber-800 font-semibold">
                          {t('checkout_otp_badge')}
                        </p>
                      )}
                    </div>
                  </label>
                )
              })}
            </div>
          </section>
        </div>

        {/* Right Column: Order Summary & Place Order */}
        <div className="space-y-4">
          <div className="card p-5 space-y-4 shadow-lg border-stone-200 sticky top-20">
            <h2 className="font-heading text-base font-bold text-stone-900">{t('checkout_order_summary')}</h2>

            {quote ? (
              <div className="space-y-3">
                {/* Items preview list */}
                <div className="max-h-48 overflow-y-auto space-y-1.5 pr-1 divide-y divide-stone-50">
                  {quote.lines.map((line) => (
                    <div key={line.variantId} className="flex justify-between pt-1.5 text-xs">
                      <span className="text-stone-700 truncate max-w-[180px]">
                        {line.name} <span className="text-stone-400">×{line.quantity}</span>
                      </span>
                      <span className="money font-semibold text-stone-900">
                        {formatMoney(line.netPaise)}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="border-t border-stone-100 pt-3 space-y-2 text-xs text-stone-600">
                  <div className="flex justify-between">
                    <span>{t('checkout_goods_subtotal')}</span>
                    <span className="money font-bold text-stone-900">
                      {formatMoney(quote.totals.goodsSubtotalPaise)}
                    </span>
                  </div>

                  {quote.totals.couponDiscountPaise > 0 && (
                    <div className="flex justify-between text-emerald-700 font-bold">
                      <span>{t('checkout_coupon')} ({quote.totals.appliedCouponCode})</span>
                      <span className="money">−{formatMoney(quote.totals.couponDiscountPaise)}</span>
                    </div>
                  )}

                  <div className="flex justify-between">
                    <span>{t('checkout_delivery_fee')}</span>
                    <span className="money font-bold">
                      {quote.totals.freeDelivery ? (
                        <span className="text-emerald-700">{isHindi ? 'मुफ़्त' : 'FREE'}</span>
                      ) : (
                        formatMoney(quote.totals.deliveryFeePaise)
                      )}
                    </span>
                  </div>

                  <div className="flex justify-between text-stone-500 text-[11px]">
                    <span>{t('checkout_gst_included')}</span>
                    <span className="money">{formatMoney(quote.totals.totalTaxPaise)}</span>
                  </div>
                </div>

                <div className="border-t border-stone-200 pt-3">
                  <div className="flex items-baseline justify-between">
                    <div>
                      <span className="font-heading text-sm font-bold text-stone-900">{t('checkout_grand_total')}</span>
                      <p className="text-[10px] text-stone-400">{t('checkout_guaranteed_price')}</p>
                    </div>
                    <span className="money text-2xl font-black text-emerald-950">
                      {formatMoney(quote.totals.totalPaise)}
                    </span>
                  </div>
                </div>

                {quote.issues.length > 0 && (
                  <ul className="rounded-xl bg-amber-50 p-2.5 space-y-1 text-[11px] text-amber-900 font-medium">
                    {quote.issues.map((issue) => (
                      <li key={issue.code}>• {issue.message}</li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <div className="py-6 text-center text-xs text-stone-400 animate-pulse">
                {t('checkout_calculating')}
              </div>
            )}

            <button
              type="button"
              className="btn-primary w-full py-3.5 text-base font-bold shadow-lg shadow-emerald-700/25 flex items-center justify-center gap-2"
              disabled={busy || !quote?.orderable || (fulfilment === 'DELIVERY' && !addressId)}
              onClick={() => void placeOrder()}
            >
              {busy ? (
                <span className="inline-flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  {t('checkout_placing')}
                </span>
              ) : (
                <>
                  <span>{t('checkout_place_order')} · {quote ? formatMoney(quote.totals.totalPaise) : ''}</span>
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>

            <div className="flex items-center justify-center gap-1.5 text-[11px] text-stone-400 pt-1">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
              <span>{t('checkout_badge_secure')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Add New Address Modal */}
      {showAddressModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/50 p-4 backdrop-blur-sm">
          <div className="card w-full max-w-md p-6 shadow-2xl">
            <div className="flex items-center justify-between border-b border-stone-100 pb-3">
              <h2 className="text-base font-bold text-stone-900">{t('addr_modal_title')}</h2>
              <button
                type="button"
                onClick={() => setShowAddressModal(false)}
                className="rounded-lg p-1 text-stone-400 hover:bg-stone-100 hover:text-stone-700"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={handleCreateAddress} className="mt-4 space-y-3">
              {addressError && (
                <p className="rounded-xl bg-red-50 p-3 text-xs text-red-700 font-semibold">{addressError}</p>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">{t('addr_recipient')}</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Rahul Sharma"
                    value={newRecipient}
                    onChange={(e) => setNewRecipient(e.target.value)}
                    className="field py-2 text-xs font-medium"
                  />
                </div>
                <div>
                  <label className="label">{t('addr_phone')}</label>
                  <input
                    type="tel"
                    required
                    placeholder="+91 98765 43210"
                    value={newPhone}
                    onChange={(e) => setNewPhone(e.target.value)}
                    className="field py-2 text-xs font-medium"
                  />
                </div>
              </div>

              <div>
                <label className="label">{t('addr_line1')}</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Flat 402, Green Meadows"
                  value={newLine1}
                  onChange={(e) => setNewLine1(e.target.value)}
                  className="field py-2 text-xs font-medium"
                />
              </div>

              <div>
                <label className="label">{t('addr_line2')}</label>
                <input
                  type="text"
                  placeholder="e.g. Near City Mall"
                  value={newLine2}
                  onChange={(e) => setNewLine2(e.target.value)}
                  className="field py-2 text-xs font-medium"
                />
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="label">{t('addr_city')}</label>
                  <input
                    type="text"
                    required
                    value={newCity}
                    onChange={(e) => setNewCity(e.target.value)}
                    className="field py-2 text-xs font-medium"
                  />
                </div>
                <div>
                  <label className="label">{t('addr_state')}</label>
                  <input
                    type="text"
                    required
                    value={newState}
                    onChange={(e) => setNewState(e.target.value)}
                    className="field py-2 text-xs font-medium"
                  />
                </div>
                <div>
                  <label className="label">{t('addr_pincode')}</label>
                  <input
                    type="text"
                    required
                    maxLength={6}
                    value={newPincode}
                    onChange={(e) => setNewPincode(e.target.value.replace(/\D/g, ''))}
                    className="field py-2 text-xs font-mono font-bold"
                  />
                </div>
              </div>

              <div className="flex gap-2 pt-3">
                <button
                  type="button"
                  onClick={() => setShowAddressModal(false)}
                  className="btn-secondary flex-1 py-2 text-xs"
                >
                  {t('addr_cancel')}
                </button>
                <button
                  type="submit"
                  disabled={addressBusy}
                  className="btn-primary flex-1 py-2 text-xs font-bold"
                >
                  {addressBusy ? t('addr_saving') : t('addr_save_btn')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Order Confirmed Celebration Popup Modal */}
      <OrderSuccessModal
        isOpen={showSuccessModal}
        order={confirmedOrder}
        onClose={() => {
          setShowSuccessModal(false)
          if (confirmedOrder) {
            navigate(`/orders/${confirmedOrder.orderId}`, { state: { justPlaced: true } })
          }
        }}
        onTrackOrder={(orderId) => {
          setShowSuccessModal(false)
          navigate(`/orders/${orderId}`, { state: { justPlaced: true } })
        }}
        onContinueShopping={() => {
          setShowSuccessModal(false)
          navigate('/')
        }}
      />
    </div>
  )
}
